import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Person as PersonIcon, Phone as PhoneIcon } from "@mui/icons-material";
import dayjs from "dayjs";

const API_BASE = process.env.REACT_APP_API_URL || "/api";
const UPLOADS_BASE = process.env.REACT_APP_UPLOADS_URL || "/uploads";

type UserRole = "admin" | "master" | "client" | string;

interface Client {
  id: number;
  name: string;
  phone: string;
  email?: string | null;
  birthday?: string | null;
  source?: string | null;
  notes?: string | null;
  deposit_amount?: number | null;
  discount_amount?: number | null;
  telegram_chat_id?: string | null;
  created_at?: string;
}

interface BookingJournalItem {
  id: number;
  start_time: string;
  end_time: string;
  status: string;
  client: { id: number; name: string; phone: string };
  master: { id: number; full_name?: string | null; phone: string };
  service: { id: number; name: string; duration_minutes: number; base_price: number };
}

interface ClientPhoto {
  id: number;
  client_id: number;
  photo_type: "reference" | "after";
  file_path: string;
  created_at: string;
}

export default function ClientsPage() {
  const userRole: UserRole = localStorage.getItem("user_role") || "client";
  const isAdmin = userRole === "admin";

  const [search, setSearch] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState<{
    name: string;
    phone: string;
    deposit_amount: string;
    discount_amount: string;
    telegram_chat_id: string;
  } | null>(null);

  const [clientBookings, setClientBookings] = useState<BookingJournalItem[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  const [clientPhotos, setClientPhotos] = useState<ClientPhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchClientPhotos = async (clientId: number) => {
    setPhotosLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/client-photos/client/${clientId}`, {
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        throw new Error("Failed to load photos");
      }
      const data = await resp.json();
      setClientPhotos(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn(e);
      setClientPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  };

  const uploadClientPhoto = async (clientId: number, photoType: "reference" | "after", file: File) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`${API_BASE}/client-photos/client/${clientId}/upload?photo_type=${photoType}`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.detail || "Не удалось загрузить фото");
      }
      await fetchClientPhotos(clientId);
    } catch (e) {
      console.warn(e);
      alert(e instanceof Error ? e.message : "Ошибка загрузки фото");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const deleteClientPhoto = async (photoId: number) => {
    try {
      const resp = await fetch(`${API_BASE}/client-photos/${photoId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.detail || "Не удалось удалить фото");
      }
      setClientPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (e) {
      console.warn(e);
      alert(e instanceof Error ? e.message : "Ошибка удаления фото");
    }
  };

  const fetchClients = async (q: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", "200");

      const resp = await fetch(`${API_BASE}/clients?${params.toString()}`, {
        headers: getAuthHeaders(),
      });

      if (!resp.ok) {
        throw new Error("Failed to load clients");
      }

      const data = await resp.json();
      setClients(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn(e);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientBookings = async (clientId: number) => {
    setBookingsLoading(true);
    try {
      // Все записи клиента (прошлые и будущие)
      const start = dayjs("2000-01-01");
      const end = dayjs().add(5, "year");

      const params = new URLSearchParams({
        client_id: String(clientId),
        start: start.toISOString(),
        end: end.toISOString(),
      });

      const resp = await fetch(`${API_BASE}/bookings/journal?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        throw new Error("Failed to load bookings");
      }
      const data = await resp.json();
      setClientBookings(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn(e);
      setClientBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  };

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void fetchClients(search);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    void fetchClients("");
  }, []);

  const openClientCard = (c: Client) => {
    setSelectedClient(c);
    setForm({
      name: c.name || "",
      phone: c.phone || "",
      deposit_amount: c.deposit_amount === null || c.deposit_amount === undefined ? "" : String(c.deposit_amount),
      discount_amount: c.discount_amount === null || c.discount_amount === undefined ? "" : String(c.discount_amount),
      telegram_chat_id: c.telegram_chat_id || "",
    });
    setCardOpen(true);
    void fetchClientBookings(c.id);
    void fetchClientPhotos(c.id);
  };

  const closeCard = () => {
    setCardOpen(false);
    setSelectedClient(null);
    setForm(null);
    setClientBookings([]);
    setClientPhotos([]);
  };

  const deleteClient = async () => {
    if (!selectedClient) return;
    if (!isAdmin) return;

    setDeleting(true);
    try {
      // force=true — удаляем клиента вместе со всеми его записями и зависимостями
      const resp = await fetch(`${API_BASE}/clients/${selectedClient.id}?force=true`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.detail || "Не удалось удалить клиента");
      }

      setClients(prev => prev.filter(x => x.id !== selectedClient.id));
      setDeleteOpen(false);
      closeCard();
    } catch (e) {
      console.warn(e);
      alert(e instanceof Error ? e.message : "Ошибка удаления");
    } finally {
      setDeleting(false);
    }
  };

  const deleteBooking = async (bookingId: number) => {
    if (!isAdmin || !selectedClient) return;
    if (!window.confirm("Удалить эту запись?")) return;
    try {
      const resp = await fetch(`${API_BASE}/bookings/${bookingId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.detail || "Не удалось удалить запись");
      }
      void fetchClientBookings(selectedClient.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Ошибка удаления записи");
    }
  };

  const saveClient = async () => {
    if (!selectedClient || !form) return;
    if (!isAdmin) return;

    setSaving(true);
    try {
      const payload: any = {
        name: form.name,
        phone: form.phone,
        deposit_amount: form.deposit_amount.trim() ? Number(form.deposit_amount) : null,
        discount_amount: form.discount_amount.trim() ? Number(form.discount_amount) : null,
        telegram_chat_id: form.telegram_chat_id.trim() ? form.telegram_chat_id.trim() : null,
      };

      const resp = await fetch(`${API_BASE}/clients/${selectedClient.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.detail || "Failed to save client");
      }

      const updated = (await resp.json()) as Client;
      setClients(prev => prev.map(x => (x.id === updated.id ? updated : x)));
      setSelectedClient(updated);
      setForm({
        name: updated.name || "",
        phone: updated.phone || "",
        deposit_amount:
          updated.deposit_amount === null || updated.deposit_amount === undefined ? "" : String(updated.deposit_amount),
        discount_amount:
          updated.discount_amount === null || updated.discount_amount === undefined ? "" : String(updated.discount_amount),
        telegram_chat_id: updated.telegram_chat_id || "",
      });
    } catch (e) {
      console.warn(e);
      alert(e instanceof Error ? e.message : "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        CRM - Клиенты
      </Typography>

      <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              label="Поиск (имя или телефон)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 320 }}
            />
            <Typography variant="body2" color="textSecondary">
              Всего клиентов: {clients.length}
              {loading ? " (загрузка...)" : ""}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <List>
        {clients.map(client => (
          <ListItem
            key={client.id}
            onClick={() => openClientCard(client)}
            sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1, cursor: "pointer" }}
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <PersonIcon fontSize="small" />
                  <Typography component="span" sx={{ fontWeight: 500 }}>
                    {client.name}
                  </Typography>
                  {client.telegram_chat_id ? (
                    <Chip label={`TG: ${client.telegram_chat_id}`} size="small" variant="outlined" />
                  ) : null}
                </Box>
              }
              secondary={
                <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                  <PhoneIcon fontSize="small" />
                  <Typography component="span">{client.phone}</Typography>
                  {isAdmin ? (
                    <Typography variant="caption" color="textSecondary">
                      • Депозит: {client.deposit_amount ?? 0} • Скидка: {client.discount_amount ?? 0}
                    </Typography>
                  ) : null}
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>

      <Dialog open={cardOpen} onClose={closeCard} maxWidth="md" fullWidth>
        <DialogTitle>Карточка клиента</DialogTitle>
        <DialogContent>
          {!selectedClient || !form ? null : (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                <TextField
                  label="Имя"
                  value={form.name}
                  onChange={e => setForm(prev => (prev ? { ...prev, name: e.target.value } : prev))}
                  disabled={!isAdmin}
                />
                <TextField
                  label="Телефон"
                  value={form.phone}
                  onChange={e => setForm(prev => (prev ? { ...prev, phone: e.target.value } : prev))}
                  disabled={!isAdmin}
                />
                <TextField
                  label="Депозит (сумма)"
                  value={form.deposit_amount}
                  onChange={e => setForm(prev => (prev ? { ...prev, deposit_amount: e.target.value } : prev))}
                  disabled={!isAdmin}
                />
                <TextField
                  label="Скидка (сумма)"
                  value={form.discount_amount}
                  onChange={e => setForm(prev => (prev ? { ...prev, discount_amount: e.target.value } : prev))}
                  disabled={!isAdmin}
                />
                <TextField
                  label="Telegram-ID (chat_id)"
                  value={form.telegram_chat_id}
                  onChange={e => setForm(prev => (prev ? { ...prev, telegram_chat_id: e.target.value } : prev))}
                  disabled={!isAdmin}
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Записи клиента
                </Typography>

                {bookingsLoading ? (
                  <Typography variant="body2" color="textSecondary">
                    Загрузка...
                  </Typography>
                ) : clientBookings.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    Записей не найдено
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Дата/время</TableCell>
                        <TableCell>Услуга</TableCell>
                        <TableCell>Мастер</TableCell>
                        <TableCell>Статус</TableCell>
                        {isAdmin && <TableCell align="right">Действия</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientBookings.map(b => (
                        <TableRow key={b.id}>
                          <TableCell>
                            {dayjs(b.start_time).format("DD.MM.YYYY HH:mm")} - {dayjs(b.end_time).format("HH:mm")}
                          </TableCell>
                          <TableCell>{b.service?.name}</TableCell>
                          <TableCell>{b.master?.full_name || `#${b.master?.id}`}</TableCell>
                          <TableCell>{b.status}</TableCell>
                          {isAdmin && (
                            <TableCell align="right">
                              <Button size="small" color="error" onClick={() => void deleteBooking(b.id)}>
                                Удалить
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Фото (референсы и после)
                </Typography>

                {photosLoading ? (
                  <Typography variant="body2" color="textSecondary">
                    Загрузка...
                  </Typography>
                ) : (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <Button variant="outlined" component="label" disabled={uploadingPhoto}>
                        Добавить референс
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (selectedClient && f) void uploadClientPhoto(selectedClient.id, "reference", f);
                            e.currentTarget.value = "";
                          }}
                        />
                      </Button>

                      <Button variant="outlined" component="label" disabled={uploadingPhoto}>
                        Добавить фото после
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (selectedClient && f) void uploadClientPhoto(selectedClient.id, "after", f);
                            e.currentTarget.value = "";
                          }}
                        />
                      </Button>
                    </Box>

                    {clientPhotos.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        Фото пока нет
                      </Typography>
                    ) : (
                      <Box
                        display="grid"
                        gridTemplateColumns={{ xs: "1fr 1fr", sm: "1fr 1fr 1fr", md: "1fr 1fr 1fr 1fr" }}
                        gap={2}
                      >
                        {clientPhotos.map((p) => (
                          <Box key={p.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                            <Box
                              component="img"
                              src={`${UPLOADS_BASE}/${p.file_path}`}
                              sx={{ width: "100%", height: 140, objectFit: "cover", display: "block" }}
                            />
                            <Box display="flex" justifyContent="space-between" alignItems="center" px={1} py={0.5}>
                              <Typography variant="caption" color="textSecondary">
                                {p.photo_type === "reference" ? "референс" : "после"}
                              </Typography>
                              <Button size="small" color="error" onClick={() => void deleteClientPhoto(p.id)}>
                                Удалить
                              </Button>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Заказы
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Модуль заказов пока не реализован
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCard}>Закрыть</Button>
          {isAdmin ? (
            <>
              <Button color="error" variant="outlined" onClick={() => setDeleteOpen(true)} disabled={!selectedClient}>
                Удалить
              </Button>
              <Button onClick={saveClient} variant="contained" disabled={saving || !selectedClient}>
                Сохранить
              </Button>
            </>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Удалить клиента?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Клиент <b>{selectedClient?.name || ""}</b> будет удалён
            {clientBookings.length > 0 ? ` вместе со всеми его записями (${clientBookings.length})` : ""}, фото
            и привязанными данными. Это действие необратимо.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Отмена
          </Button>
          <Button color="error" variant="contained" onClick={() => void deleteClient()} disabled={deleting}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
